import type { Zero, ZeroOptions } from "@rocicorp/zero";
import {
  ZeroProvider as BaseZeroProvider,
  useConnectionState as baseUseConnectionState,
  useZero as baseUseZero,
} from "@rocicorp/zero/react";
import { createContext, type ReactNode, useContext } from "react";

import type { AuthContext } from "@animus/zero-schema";
import { mutators, type Schema, schema } from "@animus/zero-schema";

type ZeroClient = Zero<Schema>;

type KvStore = ZeroOptions<Schema>["kvStore"];

const ZeroClientContext = createContext<ZeroClient | null>(null);

interface ConnectionState {
  name: string;
}

const ConnectionStateContext = createContext<ConnectionState>({
  name: "disconnected",
});

interface ZeroProviderProps {
  serverUrl: string;
  userID: string;
  role: AuthContext["role"];
  auth?: string;
  kvStore?: KvStore;
  children: ReactNode;
}

function ZeroClientBridge({ children }: { children: ReactNode }) {
  const zero = baseUseZero<Schema>();
  const connectionState = baseUseConnectionState();

  return (
    <ZeroClientContext.Provider value={zero}>
      <ConnectionStateContext.Provider value={connectionState}>
        {children}
      </ConnectionStateContext.Provider>
    </ZeroClientContext.Provider>
  );
}

export function ZeroProvider({
  serverUrl,
  userID,
  role,
  auth,
  kvStore,
  children,
}: ZeroProviderProps) {
  const context: AuthContext = { userID, role };

  return (
    <BaseZeroProvider
      auth={auth}
      cacheURL={serverUrl}
      context={context}
      kvStore={kvStore}
      mutators={mutators}
      schema={schema}
      userID={userID}
    >
      <ZeroClientBridge>{children}</ZeroClientBridge>
    </BaseZeroProvider>
  );
}

export function useZero(): ZeroClient {
  const zero = useContext(ZeroClientContext);
  if (!zero) {
    throw new Error("useZero must be used within a ZeroProvider");
  }
  return zero;
}

export function useConnectionState(): ConnectionState {
  return useContext(ConnectionStateContext);
}

export type { KvStore, ZeroClient, ZeroProviderProps };
